(() => {
  const PENDING_VERSION = 1;
  const PENDING_TTL_MS = 30 * 60 * 1000;
  const COMPLETED_DEDUP_MS = 10 * 1000;
  const memoryPending = new Map();

  const parseList = (value) => (value || '').split('|').map((item) => item.trim()).filter(Boolean);
  const parseMap = (value) => {
    const result = {};
    parseList(value).forEach((pair) => {
      const separator = pair.indexOf('=>');
      if (separator > 0) result[pair.slice(0, separator).trim()] = pair.slice(separator + 2).trim();
    });
    return result;
  };
  const parseJSON = (element, fallback) => {
    try { return JSON.parse(element ? element.textContent : ''); } catch (_) { return fallback; }
  };
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {style: 'currency', currency: 'USD'}).format(Number(cents || 0) / 100);
  const cameraCount = (variant) => {
    const source = [variant && variant.title, ...((variant && variant.options) || [])].join(' ');
    const matches = source.match(/\d+/g);
    return matches && matches.length ? Math.max(1, Number(matches[matches.length - 1])) : 1;
  };
  const asPositiveInteger = (value) => Math.max(1, Number.parseInt(value || '1', 10) || 1);
  const pendingKey = (signature) => `cv-addon-pending:v${PENDING_VERSION}:${signature}`;

  const readPending = (key) => {
    let value = memoryPending.get(key) || null;
    try { value = window.localStorage.getItem(key) || value; } catch (_) {}
    if (!value) return null;
    try {
      const parsed = typeof value === 'string' ? JSON.parse(value) : value;
      if (parsed.version !== PENDING_VERSION || parsed.expiresAt <= Date.now()) {
        clearPending(key);
        return null;
      }
      return parsed;
    } catch (_) {
      clearPending(key);
      return null;
    }
  };

  const writePending = (key, value) => {
    memoryPending.set(key, value);
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };

  const clearPending = (key) => {
    memoryPending.delete(key);
    try { window.localStorage.removeItem(key); } catch (_) {}
  };

  const cartSnapshot = async () => {
    const response = await fetch('/cart.js', {headers: {Accept: 'application/json'}});
    if (!response.ok) throw new Error('CART_READ_FAILED');
    const cart = await response.json();
    const quantities = {};
    (cart.items || []).forEach((item) => {
      const id = String(item.variant_id || item.id || '');
      if (id) quantities[id] = (quantities[id] || 0) + Number(item.quantity || 0);
    });
    return {cart, quantities};
  };

  const makeSignature = (items) => items
    .map((item) => `${item.variantId}:${item.quantity}`)
    .sort()
    .join('|');

  const announce = (status, message) => {
    if (status) status.textContent = message;
  };

  const createPending = (key, signature, items, snapshot) => {
    const requested = {};
    items.forEach((item) => {
      requested[item.variantId] = (requested[item.variantId] || 0) + item.quantity;
    });
    const targets = {};
    Object.entries(requested).forEach(([variantId, quantity]) => {
      targets[variantId] = Number(snapshot.quantities[variantId] || 0) + quantity;
    });
    const pending = {
      version: PENDING_VERSION,
      signature,
      createdAt: Date.now(),
      expiresAt: Date.now() + PENDING_TTL_MS,
      baseline: snapshot.quantities,
      targets,
      items
    };
    writePending(key, pending);
    return pending;
  };

  const remainingItems = (pending, snapshot) => pending.items.filter((item, index, all) => (
    all.findIndex((candidate) => candidate.variantId === item.variantId) === index
    && Number(snapshot.quantities[item.variantId] || 0) < Number(pending.targets[item.variantId] || 0)
  ));

  const intendedAddedCount = (pending, snapshot) => Object.keys(pending.targets).reduce((total, variantId) => {
    const baseline = Number(pending.baseline[variantId] || 0);
    const target = Number(pending.targets[variantId] || 0);
    const current = Number(snapshot.quantities[variantId] || 0);
    return total + Math.max(0, Math.min(target, current) - baseline);
  }, 0);

  const postMissingQuantity = async (item, quantity, parentForm) => {
    let body;
    let headers = {Accept: 'application/json'};
    if (item.role === 'parent') {
      body = new FormData(parentForm);
      body.set('id', item.variantId);
      body.set('quantity', String(quantity));
    } else {
      body = new URLSearchParams({id: item.variantId, quantity: String(quantity)});
      headers = {...headers, 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'};
    }
    try {
      await fetch('/cart/add.js', {method: 'POST', body, headers});
    } catch (_) {
      // The response cannot be trusted as proof of cart state. The caller reads /cart.js next.
    }
  };

  const runReconciledAdd = async ({items, parentForm, status}) => {
    const signature = makeSignature(items);
    const key = pendingKey(signature);
    let initialSnapshot;
    try {
      initialSnapshot = await cartSnapshot();
    } catch (_) {
      announce(status, 'Nothing was added because the cart could not be verified. Please try again.');
      return {complete: false, verificationFailed: false};
    }

    const existing = readPending(key);
    if (existing && existing.completed) {
      announce(status, 'This cart update was already completed. Your cart was not changed again.');
      return {complete: true, verificationFailed: false};
    }
    const isRetry = Boolean(existing && existing.signature === signature);
    const pending = isRetry ? existing : createPending(key, signature, items, initialSnapshot);
    let snapshot = initialSnapshot;

    for (const item of pending.items) {
      const current = Number(snapshot.quantities[item.variantId] || 0);
      const target = Number(pending.targets[item.variantId] || 0);
      const missing = Math.max(0, target - current);
      if (!missing) continue;
      await postMissingQuantity(item, missing, parentForm);
      try {
        snapshot = await cartSnapshot();
      } catch (_) {
        announce(status, 'We could not verify the final cart state. Review your cart before trying again.');
        return {complete: false, verificationFailed: true};
      }
    }

    const remaining = remainingItems(pending, snapshot);
    if (!remaining.length) {
      writePending(key, {
        ...pending,
        completed: true,
        completedAt: Date.now(),
        expiresAt: Date.now() + COMPLETED_DEDUP_MS
      });
      announce(status, isRetry
        ? 'The retry added only the remaining items. Your cart is complete.'
        : 'The system and selected additions were added to your cart.');
      return {complete: true, verificationFailed: false};
    }

    const remainingTitles = remaining.map((item) => item.title).join(', ');
    if (intendedAddedCount(pending, snapshot) === 0) {
      announce(status, `Nothing was added. Still needed: ${remainingTitles}. Please try again.`);
    } else {
      announce(status, `Some items are in your cart. Still not added: ${remainingTitles}. Retry here; items already added will not be duplicated.`);
    }
    writePending(key, {...pending, expiresAt: Date.now() + PENDING_TTL_MS});
    return {complete: false, verificationFailed: false};
  };

  function initialize(root) {
    if (root.dataset.cvAddonInitialized === 'true') return;
    root.dataset.cvAddonInitialized = 'true';
    const cards = Array.from(root.querySelectorAll('[data-cv-addon-card]'));
    const parentVariants = parseJSON(root.querySelector('[data-cv-parent-variants]'), []);
    const parentIds = new Set(parentVariants.map((variant) => String(variant.id)));
    const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
    const parentForm = forms.find((form) => parentIds.has(String(form.querySelector('[name="id"]')?.value))) || forms.find((form) => form.querySelector('[name="id"]'));
    const status = root.querySelector('[data-cv-addon-status]');

    const currentParentId = () => {
      const inputValue = parentForm?.querySelector('[name="id"]')?.value;
      const urlValue = new URL(window.location.href).searchParams.get('variant');
      return String(inputValue || urlValue || root.dataset.currentParentVariantId || '');
    };

    const update = () => {
      const parentId = currentParentId();
      const parentVariant = parentVariants.find((variant) => String(variant.id) === parentId) || parentVariants[0];
      let visibleCount = 0;
      cards.forEach((card) => {
        const applicable = parseList(card.dataset.applicableParentVariantIds);
        const mapping = parseMap(card.dataset.accessoryVariantMap);
        const variants = parseJSON(card.querySelector('[data-cv-addon-variants]'), []);
        const sku = mapping[parentId];
        const accessoryVariant = variants.find((variant) => variant.sku === sku);
        const checkbox = card.querySelector('[data-cv-addon-checkbox]');
        const quantity = card.querySelector('[data-cv-addon-quantity]');
        const quantityWrap = card.querySelector('[data-cv-addon-quantity-wrap]');
        const quantityNote = card.querySelector('[data-cv-addon-quantity-note]');
        const availability = card.querySelector('[data-cv-addon-availability]');
        const isApplicable = applicable.includes(parentId) && Boolean(accessoryVariant);
        card.hidden = !isApplicable;
        if (!isApplicable) {
          checkbox.checked = false;
          checkbox.disabled = true;
          return;
        }
        visibleCount += 1;
        checkbox.value = String(accessoryVariant.id);
        checkbox.disabled = !accessoryVariant.available;
        if (!accessoryVariant.available) checkbox.checked = false;
        card.querySelector('[data-cv-addon-price]').textContent = money(accessoryVariant.price);
        availability.textContent = accessoryVariant.available ? 'Available' : 'Currently unavailable';

        const logic = card.dataset.quantityLogic;
        if (logic === 'USER_SELECTABLE') {
          quantityWrap.hidden = false;
          quantity.readOnly = false;
          quantityNote.textContent = '';
        } else {
          const nextQuantity = logic === 'PER_CAMERA' ? cameraCount(parentVariant) : 1;
          quantity.value = String(nextQuantity);
          quantity.readOnly = true;
          quantityWrap.hidden = false;
          quantityNote.textContent = logic === 'PER_CAMERA' ? ` Matches the selected ${nextQuantity}-camera configuration.` : ' Fixed at one.';
        }
      });
      root.hidden = visibleCount === 0;
      root.dataset.currentParentVariantId = parentId;
    };

    update();
    document.addEventListener('change', () => window.requestAnimationFrame(update));
    document.addEventListener('variant:change', () => window.requestAnimationFrame(update));
    window.addEventListener('popstate', update);

    if (!parentForm || parentForm.dataset.cvAddonCartBound === 'true') return;
    parentForm.dataset.cvAddonCartBound = 'true';
    parentForm.addEventListener('submit', async (event) => {
      const selected = cards.filter((card) => !card.hidden && card.querySelector('[data-cv-addon-checkbox]')?.checked);
      if (!selected.length) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (parentForm.dataset.cvAddonCartInFlight === 'true') {
        announce(status, 'Your cart update is already in progress.');
        return;
      }
      parentForm.dataset.cvAddonCartInFlight = 'true';

      const parentId = currentParentId();
      const parentQuantity = asPositiveInteger(parentForm.querySelector('[name="quantity"]')?.value);
      const items = [{
        variantId: parentId,
        quantity: parentQuantity,
        title: 'System',
        role: 'parent'
      }];
      selected.forEach((card) => {
        items.push({
          variantId: String(card.querySelector('[data-cv-addon-checkbox]').value),
          quantity: asPositiveInteger(card.querySelector('[data-cv-addon-quantity]')?.value),
          title: card.dataset.accessoryTitle || 'Selected addition',
          role: 'accessory'
        });
      });

      const buttons = Array.from(parentForm.querySelectorAll('[type="submit"]'));
      buttons.forEach((button) => { button.disabled = true; });
      announce(status, 'Checking your cart and adding only missing items…');
      let keepDisabled = false;
      let navigating = false;
      const action = async () => {
        const result = await runReconciledAdd({items, parentForm, status});
        keepDisabled = result.verificationFailed;
        if (result.complete) {
          navigating = true;
          window.location.assign('/cart');
        }
      };

      try {
        const lockName = `cv-addon-cart:${makeSignature(items)}`;
        if (navigator.locks && navigator.locks.request) {
          await navigator.locks.request(lockName, action);
        } else {
          await action();
        }
      } finally {
        parentForm.dataset.cvAddonCartInFlight = 'false';
        if (!navigating && !keepDisabled) buttons.forEach((button) => { button.disabled = false; });
      }
    }, true);
  }

  const initializeAll = (scope = document) => scope.querySelectorAll('[data-cv-addon-section]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll()); else initializeAll();
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
